import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CONVERSATIONS } from "@/constants/testIds";

// Fix O (2026-09-30): plantillas aprobadas de WhatsApp. Pasadas 24 horas del
// ultimo mensaje del cliente (o para escribirle a alguien por primera vez)
// WhatsApp solo deja mandar una plantilla: se elige, se completan sus datos y
// se ve como le va a llegar el mensaje antes de mandarlo.
const PARAM_RE = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;
const LABELS = { nombre: "Nombre", tratamiento: "Tratamiento", fecha: "Fecha", hora: "Hora", instrucciones: "Instrucciones" };  // Fix AC: fecha y hora; Fix AV: instrucciones

export const templateKey = (t) => `${t.name}|${t.language}`;
export const firstName = (name) => (name || "").trim().split(/\s+/)[0] || "";

export function renderTemplate(template, values) {
  const body = (template.body || "").replace(PARAM_RE, (_, key) => values[key] || `[${key}]`);
  return [template.header, body, template.footer].filter(Boolean).join("\n\n");
}

/** Estado del formulario de plantilla. `preferred`: comienzo del nombre de la
 * plantilla que se elige primero; `suggestions`: valores sugeridos por dato;
 * `language`: idioma preferido (el de la conversacion). */
export function useTemplateForm(open, preferred, suggestions, language = "es") {
  const [state, setState] = useState({ status: "loading", templates: [], error: "" });
  const [chosen, setChosen] = useState("");
  const [edited, setEdited] = useState({});

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setState({ status: "loading", templates: [], error: "" });
    setChosen("");
    setEdited({});
    api.get("/conversations/templates")
      .then(({ data }) => { if (alive) setState({ status: "ready", templates: data, error: "" }); })
      .catch((err) => {
        if (!alive) return;
        // 400 = el canal no es WhatsApp de Meta (todavia no se hizo el cambio de numero)
        setState({ status: err.response?.status === 400 ? "unavailable" : "error", templates: [], error: formatApiErrorDetail(err.response?.data?.detail) });
      });
    return () => { alive = false; };
  }, [open]);

  const { templates } = state;
  const byName = (t) => Boolean(preferred) && t.name.startsWith(preferred);
  const byLanguage = (t) => (t.language || "").startsWith(language || "es");
  const fallback = templates.find((t) => byName(t) && byLanguage(t)) || templates.find(byName) || templates.find(byLanguage) || templates[0];
  const key = chosen || (fallback ? templateKey(fallback) : "");
  const template = templates.find((t) => templateKey(t) === key) || null;
  const values = {};
  for (const p of template?.params || []) values[p] = p in edited ? edited[p] : (suggestions?.[p] || "");
  return {
    ...state,
    key,
    template,
    values,
    text: template ? renderTemplate(template, values) : "",
    complete: Boolean(template) && template.params.every((p) => values[p].trim()),
    choose: (k) => { setChosen(k); setEdited({}); },
    setValue: (p, v) => setEdited((prev) => ({ ...prev, [p]: v })),
  };
}

export default function TemplateFields({ form }) {
  if (form.status === "loading") return <p className="text-sm text-muted-foreground">Cargando plantillas...</p>;
  if (form.status !== "ready") return <p className="text-sm text-destructive">{form.error || "No se pudieron cargar las plantillas."}</p>;
  if (!form.templates.length) {
    return <p className="text-sm text-muted-foreground" data-testid={CONVERSATIONS.templateEmpty}>Todavia no hay plantillas aprobadas en WhatsApp.</p>;
  }
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm text-muted-foreground mb-1 block">Plantilla</Label>
        <select
          value={form.key}
          onChange={(e) => form.choose(e.target.value)}
          className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
          data-testid={CONVERSATIONS.templateSelect}
        >
          {form.templates.map((t) => (
            <option key={templateKey(t)} value={templateKey(t)}>{t.name.replaceAll("_", " ")} ({t.language})</option>
          ))}
        </select>
      </div>
      {form.template.params.map((p) => (
        <div key={p}>
          <Label className="text-sm text-muted-foreground mb-1 block">{LABELS[p] || (/^\d+$/.test(p) ? `Dato ${p}` : p.replaceAll("_", " "))}</Label>
          <Input value={form.values[p]} onChange={(e) => form.setValue(p, e.target.value)} data-testid={`${CONVERSATIONS.templateParamPrefix}${p}`} />
        </div>
      ))}
      <div>
        <Label className="text-sm text-muted-foreground mb-1 block">Asi le va a llegar</Label>
        <p className="text-sm whitespace-pre-wrap bg-muted rounded-lg px-3 py-2" data-testid={CONVERSATIONS.templatePreview}>{form.text}</p>
      </div>
    </div>
  );
}
